import React, { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import {
  ClipboardList, Package, Truck, CheckCircle2,
  Clock, Printer, RefreshCw, ChevronRight, User, FileText
} from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";
import { formatDate, formatDateTime } from "../utils/format";

const FULFILLMENT_STATUSES = [
  { key: "pendiente",   label: "Pendiente de surtir", icon: Clock,         bg: "bg-amber-50",   border: "border-amber-200",   badge: "bg-amber-100 text-amber-800",   dot: "bg-amber-400" },
  { key: "en_proceso",  label: "En proceso",           icon: Package,       bg: "bg-blue-50",    border: "border-blue-200",    badge: "bg-blue-100 text-blue-800",     dot: "bg-blue-400" },
  { key: "listo",       label: "Listo para enviar",    icon: CheckCircle2,  bg: "bg-brand-50", border: "border-brand-200", badge: "bg-brand-100 text-brand-800", dot: "bg-brand-400" },
  { key: "enviado",     label: "Enviado/Entregado",    icon: Truck,         bg: "bg-slate-50",   border: "border-slate-200",   badge: "bg-slate-100 text-slate-600",   dot: "bg-slate-400" },
];

const NEXT_STATUS = {
  pendiente:  "en_proceso",
  en_proceso: "listo",
  listo:      "enviado",
};

const NEXT_LABEL = {
  pendiente:  "Iniciar surtido",
  en_proceso: "Marcar listo",
  listo:      "Marcar enviado",
};

const STATUS_MAP = Object.fromEntries(FULFILLMENT_STATUSES.map((s) => [s.key, s]));

function OrderCard({ order, onStatusChange }) {
  const [updating, setUpdating] = useState(false);
  const st = STATUS_MAP[order.fulfillment_status || "pendiente"];
  const nextSt = NEXT_STATUS[order.fulfillment_status || "pendiente"];

  const handleAdvance = async () => {
    if (!nextSt) return;
    setUpdating(true);
    try {
      await api.patch(`/fulfillment/${order.id}/status`, { fulfillment_status: nextSt });
      toast.success(`Pedido ${order.folio} → ${STATUS_MAP[nextSt]?.label}`);
      onStatusChange();
    } catch {
      toast.error("Error al actualizar estado");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div
      data-testid={`order-card-${order.id}`}
      className={`rounded-xl border ${st.border} ${st.bg} p-4 flex flex-col gap-3 hover:shadow-md transition-shadow`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-bold text-slate-800">{order.folio}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.badge}`}>
              {st.label}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-1 text-slate-600">
            <User size={12} />
            <span className="text-sm font-medium">{order.customer_name || "Sin cliente"}</span>
          </div>
          {order.customer_phone && (
            <div className="text-xs text-slate-500 mt-0.5">Tel: {order.customer_phone}</div>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-xs text-slate-500">{formatDate(order.created_at)}</div>
        </div>
      </div>

      {/* Products */}
      <div className="bg-white/70 rounded-lg p-3 space-y-1.5">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
          Productos a empacar
        </div>
        {(order.items || []).map((item, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${st.dot}`} />
              <span className="text-slate-800 font-medium">{item.product_name}</span>
            </div>
            <span className="font-bold text-slate-900 tabular-nums">
              {item.quantity} <span className="font-normal text-slate-500">{item.unit}</span>
            </span>
          </div>
        ))}
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="text-xs text-slate-500 bg-white/60 rounded-lg px-3 py-2 italic">
          Nota: {order.notes}
        </div>
      )}

      {/* Actions */}
      {nextSt && (
        <button
          onClick={handleAdvance}
          disabled={updating}
          data-testid={`advance-${order.id}`}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-colors bg-slate-900 hover:bg-slate-700 text-white disabled:opacity-50"
        >
          {updating ? (
            <RefreshCw size={14} className="animate-spin" />
          ) : (
            <ChevronRight size={14} />
          )}
          {updating ? "Actualizando..." : NEXT_LABEL[order.fulfillment_status || "pendiente"]}
        </button>
      )}
      {!nextSt && (
        <div className="text-center text-xs text-slate-400 py-1">Entregado</div>
      )}
    </div>
  );
}

function PickingReport({ data, onClose }) {
  const printRef = useRef();
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Modal header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Reporte de Picking — Almacén</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {data.total_orders} pedidos · Generado {formatDateTime(data.generated_at)} por {data.generated_by}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Printer size={15} /> Imprimir
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>

        {/* Printable content */}
        <div ref={printRef} className="overflow-y-auto flex-1 p-6 print:p-8">
          {/* Print header */}
          <div className="print:block hidden mb-6 pb-4 border-b border-slate-300">
            <div className="text-2xl font-bold text-slate-900">Lista de Empaque — Almacén</div>
            <div className="text-sm text-slate-500 mt-1">
              Fecha: {formatDateTime(data.generated_at)} · Preparado por: {data.generated_by}
            </div>
          </div>

          {/* Section 1: Consolidado por producto */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 bg-brand-600 rounded text-white text-xs font-bold flex items-center justify-center">1</div>
              <h3 className="font-bold text-slate-900">Resumen consolidado por producto</h3>
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                {data.consolidated.length} productos
              </span>
            </div>
            <table className="w-full text-sm border border-slate-200 rounded-xl overflow-hidden">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Producto</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total a preparar</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Para pedidos</th>
                </tr>
              </thead>
              <tbody>
                {data.consolidated.length === 0 ? (
                  <tr><td colSpan={3} className="text-center py-8 text-slate-400">Sin productos pendientes</td></tr>
                ) : data.consolidated.map((item, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-slate-900">{item.product_name}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-2xl font-bold text-brand-700">{item.total_quantity}</span>
                      <span className="text-slate-500 ml-1">{item.unit}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {item.orders.map((o, oi) => (
                          <span key={oi} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-mono">
                            {o.folio} ({o.quantity}{item.unit})
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Section 2: Detalle por pedido */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 bg-slate-700 rounded text-white text-xs font-bold flex items-center justify-center">2</div>
              <h3 className="font-bold text-slate-900">Detalle por pedido</h3>
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                {data.total_orders} pedidos
              </span>
            </div>

            {/* Group lines by folio */}
            {(() => {
              const byFolio = {};
              data.order_lines.forEach((line) => {
                if (!byFolio[line.folio]) {
                  byFolio[line.folio] = {
                    folio: line.folio,
                    customer_name: line.customer_name,
                    fulfillment_status: line.fulfillment_status,
                    notes: line.notes,
                    items: []
                  };
                }
                byFolio[line.folio].items.push(line);
              });
              return Object.values(byFolio).map((order, i) => {
                const st = STATUS_MAP[order.fulfillment_status] || STATUS_MAP["pendiente"];
                return (
                  <div key={i} className={`mb-4 rounded-xl border ${st.border} ${st.bg} overflow-hidden`}>
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/50">
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-slate-800">{order.folio}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.badge}`}>{st.label}</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-700">
                        <User size={13} />
                        <span className="font-semibold text-sm">{order.customer_name}</span>
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-white/40">
                          <th className="text-left px-4 py-2 text-xs text-slate-500 font-semibold">Producto</th>
                          <th className="text-right px-4 py-2 text-xs text-slate-500 font-semibold">Cantidad</th>
                          <th className="text-right px-4 py-2 text-xs text-slate-500 font-semibold print:block hidden">
                            <span className="flex items-center justify-end gap-1">Revisado</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item, ii) => (
                          <tr key={ii} className="border-t border-white/50">
                            <td className="px-4 py-2.5 font-medium text-slate-800">{item.product_name}</td>
                            <td className="px-4 py-2.5 text-right font-bold text-slate-900">
                              {item.quantity} <span className="font-normal text-slate-500">{item.unit}</span>
                            </td>
                            {/* Print-only checkbox */}
                            <td className="px-4 py-2.5 text-right print:block hidden">
                              <div className="w-5 h-5 border-2 border-slate-400 rounded inline-block" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {order.notes && (
                      <div className="px-4 py-2 text-xs text-slate-500 italic border-t border-white/50">
                        Nota: {order.notes}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Surtido() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pendiente");
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Load all non-shipped first, then shipped separately
      const [active, shipped] = await Promise.all([
        api.get("/fulfillment"),
        api.get("/fulfillment?fulfillment_status=enviado"),
      ]);
      setOrders([
        ...active.data.map((o) => ({ ...o, fulfillment_status: o.fulfillment_status || "pendiente" })),
        ...shipped.data,
      ]);
    } catch {
      toast.error("Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handlePickingReport = async () => {
    setLoadingReport(true);
    try {
      const res = await api.get("/fulfillment/picking-report");
      setReportData(res.data);
      setShowReport(true);
    } catch {
      toast.error("Error al generar reporte");
    } finally {
      setLoadingReport(false);
    }
  };

  const byStatus = {};
  FULFILLMENT_STATUSES.forEach((s) => {
    byStatus[s.key] = orders.filter((o) => (o.fulfillment_status || "pendiente") === s.key);
  });

  const pendingCount = byStatus["pendiente"].length + byStatus["en_proceso"].length;

  return (
    <div className="space-y-6" data-testid="surtido-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Surtido y Despacho</h1>
          <p className="text-slate-500 text-sm mt-1">
            Gestiona el surtido de pedidos y genera listas de empaque para almacén
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg text-sm font-medium transition-colors"
          >
            <RefreshCw size={14} /> Actualizar
          </button>
          <button
            onClick={handlePickingReport}
            disabled={loadingReport}
            data-testid="picking-report-btn"
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-60"
          >
            <Printer size={15} />
            {loadingReport ? "Generando..." : "Reporte de Almacén"}
          </button>
        </div>
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap gap-3">
        {FULFILLMENT_STATUSES.map((s) => {
          const count = byStatus[s.key].length;
          return (
            <button
              key={s.key}
              onClick={() => setActiveTab(s.key)}
              data-testid={`tab-${s.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                activeTab === s.key
                  ? `${s.bg} ${s.border} text-slate-900`
                  : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
              }`}
            >
              <s.icon size={15} />
              {s.label}
              <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                activeTab === s.key ? s.badge : "bg-slate-100 text-slate-600"
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Alert for pending */}
      {pendingCount > 0 && activeTab !== "enviado" && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <Clock size={16} className="text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800 font-medium">
            {pendingCount} pedido{pendingCount !== 1 ? "s" : ""} pendiente{pendingCount !== 1 ? "s" : ""} por surtir
          </span>
        </div>
      )}

      {/* Cards grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
          Cargando pedidos...
        </div>
      ) : byStatus[activeTab].length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-slate-400 gap-2">
          <ClipboardList size={32} className="opacity-30" />
          <span className="text-sm">Sin pedidos en este estado</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {byStatus[activeTab].map((order) => (
            <OrderCard key={order.id} order={order} onStatusChange={load} />
          ))}
        </div>
      )}

      {/* Picking Report Modal */}
      {showReport && reportData && (
        <PickingReport data={reportData} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
}
